import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getSchedulerAllScheduleThunk,
    getSchedulerDayScheduleThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { Market, WorkStatus } from "tm-shared/types";

import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { sortMarkets } from "@/hooks/useFilteredMarkets";
import { getMarketInfoCollectionUnplannedThunk } from "@/store/slices/work";

export const useUnplannedMarkets = () => {
    const dispatch = useAppDispatch();
    const { id, role } = useAppSelector((state) => state.user);
    const { activeSchedule } = useAppSelector((state) => state.sv);
    const { marketsSortOption } = useAppSelector((state) => state.ui.workPlan);
    const {
        marketIds,
        marketExtraIds,
        workStatus,
        currentRouteList,
        unplannedMarkets,
    } = useAppSelector((state) => state.work);

    const [selectedDay, setSelectedDay] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedMarkets, setSelectedMarkets] = useState<Market[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [loadingSchedule, setLoadingSchedule] = useState(false);
    const [loadingMarkets, setLoadingMarkets] = useState(false);

    const fetchedMarketIdsRef = useRef<Set<string>>(new Set());
    const localLoading = loadingSchedule || loadingMarkets;

    const uniqueMarkets = useMemo(() => {
        if (!unplannedMarkets?.markets) return [];

        const marketIdsSet = new Set(marketIds);

        return Object.values(unplannedMarkets.markets)
            .map((market) => ({
                ...market,
                days: currentRouteList.find(
                    ({ marketId }) => marketId === market.id,
                )?.days,
            }))
            .filter((market) => !marketIdsSet.has(market.id))
            .filter(
                (market) =>
                    selectedDay === 0 || market.days?.includes(selectedDay),
            );
    }, [unplannedMarkets?.markets, currentRouteList, marketIds, selectedDay]);

    const filteredMarkets = useMemo(() => {
        if (!searchTerm) {
            return sortMarkets(
                uniqueMarkets,
                marketsSortOption?.value || "route",
            );
        }

        const lower = searchTerm.toLowerCase();

        return sortMarkets(
            uniqueMarkets.filter(
                ({ name, contragent }) =>
                    name.toLowerCase().includes(lower) ||
                    contragent.name.toLowerCase().includes(lower),
            ),
            marketsSortOption?.value || "route",
        );
    }, [uniqueMarkets, searchTerm, marketsSortOption]);

    const toggleMarketSelection = useCallback((market: Market) => {
        setSelectedMarkets((prev) => {
            const isSelected = prev.some((m) => m.id === market.id);
            return isSelected
                ? prev.filter((m) => m.id !== market.id)
                : [...prev, market];
        });
    }, []);

    useEffect(() => {
        if (!id || workStatus !== WorkStatus.StartWork) return;

        const fetchSchedule = async () => {
            setLoadingSchedule(true);

            try {
                const today = dayjs().format(backendDateFormat.date);

                await Promise.all([
                    dispatch(
                        getSchedulerDayScheduleThunk({
                            agentId: isManagerRole(role) ? activeSchedule : id,
                            date: today,
                        }),
                    ).unwrap(),
                    dispatch(
                        getSchedulerAllScheduleThunk({
                            agentId: isManagerRole(role) ? activeSchedule : id,
                        }),
                    ).unwrap(),
                ]);
            } catch (error) {
                console.error("Failed to fetch schedule:", error);
            } finally {
                setLoadingSchedule(false);
            }
        };

        fetchSchedule();
    }, [id, workStatus, dispatch]);

    useEffect(() => {
        if (!currentRouteList?.length) return;

        const marketIdsSet = new Set(marketIds);
        const newMarketIds = currentRouteList
            .map(({ marketId }) => marketId)
            .filter(
                (id) =>
                    !marketIdsSet.has(id) &&
                    !fetchedMarketIdsRef.current.has(id),
            );

        if (!newMarketIds.length) return;

        const fetchMarketsInfo = async () => {
            setLoadingMarkets(true);

            newMarketIds.forEach((id) => fetchedMarketIdsRef.current.add(id));

            try {
                await dispatch(
                    getMarketInfoCollectionUnplannedThunk({
                        marketIds: newMarketIds,
                    }),
                ).unwrap();
            } catch (error) {
                console.error("Failed to fetch markets info:", error);
                newMarketIds.forEach((id) =>
                    fetchedMarketIdsRef.current.delete(id),
                );
            } finally {
                setLoadingMarkets(false);
            }
        };

        fetchMarketsInfo();
    }, [currentRouteList, marketIds, dispatch]);

    return {
        filteredMarkets,
        selectedDay,
        setSelectedDay,
        searchTerm,
        setSearchTerm,
        selectedMarkets,
        toggleMarketSelection,
        openModal,
        setOpenModal,
        marketExtraIds,
        userId: id,
        loading: localLoading,
    };
};
